import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  'A free, copy-paste fundraising letter template for athletes — plus how to personalize it, who to send it to, and how to follow up so it actually gets read.';

const FAQS = [
  {
    q: 'What should a sports fundraising letter include?',
    a: 'Four things: who you are and what you play, a specific goal (season fees, travel, gear), what the money is for and by when, and one clear way to give — a single link, not a form or a "venmo me" message. Specific beats generic every time.',
  },
  {
    q: 'How long should a fundraising letter be?',
    a: "Short. Three to five sentences for a text or email, a little more for a printed letter to a business. People skim — lead with the specific, personal reason before the ask, and put the link where it can't be missed.",
  },
  {
    q: 'Who should I send my fundraising letter to?',
    a: 'Start with people who already know you — family, close friends, neighbors — before reaching out to local businesses. Warm contacts convert far better than cold ones, and a first wave of family support makes the page look active when you do ask a business or a wider circle.',
  },
  {
    q: 'Should I ask for a one-time gift or recurring support in the letter?',
    a: "Recurring is usually the easier ask and the better outcome. Asking a supporter for $15–$20 a month feels smaller than asking for $200 at once, and it keeps covering costs as they come up across a season instead of running out after the first expense.",
  },
  {
    q: "Do I need to follow up if someone doesn't respond?",
    a: "Yes — one polite follow-up two weeks later, and an update after you use the support (a photo from a meet, a thank-you). Most people mean to give and get busy; a short reminder is not pushy, it is the thing that actually gets it seen.",
  },
];

const FundraisingLetterTemplate: React.FC = () => {
  usePageMeta(
    'Sports Fundraising Letter Template: A Free Letter You Can Copy',
    ARTICLE_DESCRIPTION,
    {
      article: true,
      image: '/resources/coach-and-athlete.jpg',
      breadcrumbs: [
        { name: 'Resources', path: '/resources' },
        { name: 'Fundraising Letter Template' },
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    }
  );
  const TOC_SECTIONS = [
    {
      id: "the-template",
      text: "The template"
    },
    {
      id: "1-make-the-first-line-about-them-not-the-ask",
      text: "1. Make the first line about them, not the ask"
    },
    {
      id: "2-name-a-real-number",
      text: "2. Name a real number"
    },
    {
      id: "3-lead-with-recurring-not-a-lump-sum",
      text: "3. Lead with recurring, not a lump sum"
    },
    {
      id: "4-put-the-link-where-it-can-t-be-missed",
      text: "4. Put the link where it can't be missed"
    },
    {
      id: "5-send-it-more-than-once",
      text: "5. Send it more than once"
    },
    {
      id: "where-sport-fund-fits",
      text: "Where Sport Fund fits"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        Sports fundraising letter template: a free letter you can copy (2026)
      </h1>
      <p className="text-gray-500 mb-8 text-lg">
        The hardest part of fundraising is usually the first message. Here's a template that works for family,
        friends and local businesses — plus how to make it your own.
      </p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          A good fundraising letter isn't a sales pitch — it's a specific, honest update from someone the reader
          already cares about. The template below covers the four things every effective ask needs: who you are,
          what you're raising for, why it matters right now, and one clear way to help. Swap in your own details
          and send it as a text, an email, or a printed note.
        </p>

        <h2 id="the-template">The template</h2>
        <div className="not-prose bg-gray-50 border border-gray-200 rounded-lg p-6 my-6 whitespace-pre-line text-gray-800">
          {`Hi [Name],

I wanted to share something I'm excited about — I made [team/goal, e.g. "the varsity squad" or "the regional qualifying meet"], and I'm raising support to help cover this season's costs (registration, travel and gear run about $[amount]).

If you're able to chip in, even $[15]/month makes a real difference — it goes straight to covering costs as they come up through the season, and every bit helps. You can support me here:

[your Sport Fund link]

Thank you for being part of this — I'll keep you posted on how the season goes.

[Your name]`}
        </div>
        <p>
          For a local business, keep the structure but add one line about the exchange: a shoutout on your page, a
          logo mention, or a photo from the season. Businesses say yes more often when the ask is small, specific,
          and easy to say no to gracefully.
        </p>

        <h2 id="1-make-the-first-line-about-them-not-the-ask">1. Make the first line about them, not the ask</h2>
        <p>
          "Hi [Name]" plus one specific, true sentence about your relationship or their support in the past does
          more work than any line about the fundraiser itself. People give to people, not to campaigns.
        </p>

        <h2 id="2-name-a-real-number">2. Name a real number</h2>
        <p>
          "Support my season" is easy to skim past. "Registration, travel and gear run about $1,200 this season" is
          concrete enough to picture — and specific numbers consistently outperform vague asks.
        </p>

        <h2 id="3-lead-with-recurring-not-a-lump-sum">3. Lead with recurring, not a lump sum</h2>
        <p>
          A single $200 ask is a big decision for most people. "$15 a month" is an easy yes, and across a season it
          usually adds up to more than a one-time gift — while continuing to cover costs as they land instead of
          running out halfway through.
        </p>

        <h2 id="4-put-the-link-where-it-can-t-be-missed">4. Put the link where it can't be missed</h2>
        <p>
          One clear line with your fund page link, not buried in a paragraph. If someone has to hunt for how to
          give, most won't.
        </p>

        <h2 id="5-send-it-more-than-once">5. Send it more than once</h2>
        <p>
          A single message is easy to miss in a busy inbox or group chat. A short, friendly follow-up two weeks
          later — and a thank-you update once you've used the support — reaches people who meant to give and got
          busy. That's not pushy; it's usually what actually gets it seen.
        </p>

        <h2 id="where-sport-fund-fits">Where Sport Fund fits</h2>
        <p>
          Your letter is only as good as where it sends people. A Sport Fund page gives you one link that's built to
          convert a "yes" into an actual gift:
        </p>
        <ul>
          <li>
            <strong>You keep 100% of every donation.</strong> The small platform fee (roughly 5–7%) is shown up
            front and added on top for the supporter — never taken out of what you raise.
          </li>
          <li>
            <strong>Recurring by design</strong>, so the "$15/month" line in your letter is a one-click setup for
            the person reading it, not a manual reminder they have to keep.
          </li>
          <li>
            <strong>Payouts go straight to your family's bank account</strong> — no club, no middleman.
          </li>
        </ul>
        <p>
          <Link to="/signup-creator" className="text-blue-600">
            Start a free fund page
          </Link>{' '}
          to get your link before you send your first letter.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/sponsorship-letter-template" className="text-blue-600">
            Sports Sponsorship Letter Template: A Free Letter for Local Businesses
          </Link>{' '}
          — a separate template built specifically for asking businesses, not family and friends.
        </p>
        <p>
          <Link to="/resources/how-to-fundraise-for-a-sports-season" className="text-blue-600">
            How to Fundraise for Your Sports Season: 7 Honest Ways That Actually Work
          </Link>{' '}
          — more ways to raise support beyond the letter itself.
        </p>
        <p>
          <Link to="/resources/thank-you-message-templates" className="text-blue-600">
            Thank-You Message Templates for Sports Fundraising Supporters
          </Link>{' '}
          — what to send once someone actually gives.
        </p>
        <ArticleFeedback articleSlug="fundraising-letter-template" />
      </article>
    </div>
  );
};

export default FundraisingLetterTemplate;
